// client/src/pages/GenerateReport/GenerateReport.jsx 수정 버전

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/main/MainLayout';
import GenerateModal from '../../components/GenerateModal/GenerateModal';
import * as S from './GenerateReport.style';
import { fileAPI, reportsAPI, inputDataAPI } from '../../utils/axios';

const GenerateReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // URL state에서 Input Data 정보 가져오기
  const inputDataInfo = location.state?.inputDataInfo;
  
  const [selectedFile, setSelectedFile] = useState('');
  const [selectedTables, setSelectedTables] = useState([]);
  const [selectAllTables, setSelectAllTables] = useState(false);
  const [reportFileName, setReportFileName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState('');
  const [generationProgress, setGenerationProgress] = useState(null);
  
  // 🆕 보고서 제목 상태 추가
  const [reportTitle, setReportTitle] = useState('');
  
  // 과년도 데이터 관련 상태
  const [baseYear, setBaseYear] = useState(new Date().getFullYear().toString());
  const [yearlyFiles, setYearlyFiles] = useState({});

  // 병원 목록 데이터 관련 상태
  const [selectedMembersFile, setSelectedMembersFile] = useState('');
  
  // 로딩 상태 추가
  const [isLoading, setIsLoading] = useState(true);
  const [detailedInputData, setDetailedInputData] = useState(null);
  
  // 사용 가능한 Raw 데이터 파일 목록
  const [availableFiles, setAvailableFiles] = useState([]);
  
  // Input Data의 테이블 목록
  const [availableTables, setAvailableTables] = useState([]);

  useEffect(() => {
    // Input Data 정보가 없으면 Generate 페이지로 돌아가기
    if (!inputDataInfo || !inputDataInfo.input_data_id) {
      console.warn('[GENERATE_REPORT]', 'No input data info provided, redirecting to Generate page');
      navigate('/generate');
      return;
    }

    console.log('[GENERATE_REPORT]', 'Input data info from location:', inputDataInfo);
    
    // 서버에서 최신 데이터 로드
    loadDetailedInputData();
    
  }, [inputDataInfo, navigate]);

  // 상세 Input Data 정보 로드
  const loadDetailedInputData = async () => {
    if (!inputDataInfo?.input_data_id) return;
    
    setIsLoading(true);
    
    try {
      console.log('[GENERATE_REPORT]', 'Loading detailed input data:', inputDataInfo.input_data_id);
      
      const result = await inputDataAPI.getDetail(inputDataInfo.input_data_id);
      
      if (result.success) {
        const detailedData = result.data;
        setDetailedInputData(detailedData);
        
        console.log('[GENERATE_REPORT]', 'Detailed input data loaded:', detailedData);
        
        // 보고서 파일명 초기화
        setReportFileName('report');
        
        // 테이블 목록 설정
        const tables = detailedData.tables_info || [];
        const formattedTables = tables.map(table => ({
          id: table.id,
          title: table.title,
          sheet_name: table.sheet_name,
          contents_name: table.contents_name,
          table_type: table.table_type
        }));
        
        setAvailableTables(formattedTables);
        console.log('[GENERATE_REPORT]', 'Available tables:', formattedTables);
        
        // Raw 데이터 파일 목록 로드
        await loadAvailableFiles();
        
      } else {
        throw new Error(result.message);
      }
      
    } catch (error) {
      console.error('[GENERATE_REPORT]', 'Load detailed data error:', error);
      alert('보고서 정보를 불러오는 중 오류가 발생했습니다.');
      navigate('/generate');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableFiles = async () => {
    try {
      console.log('[GENERATE_REPORT]', 'Loading available files...');
      
      const result = await fileAPI.getFileList({
        status: 'active',
        limit: 100,
        sort_by: 'uploaded_at',
        sort_order: 'desc'
      });
      
      if (result.success) {
        const files = result.data.files || [];
        console.log('[GENERATE_REPORT]', 'Raw files from API:', files);
        
        // Excel 파일만 필터링
        // const excelFiles = files.filter(file => 
        //   file.file_type === 'xlsx' || file.file_type === 'xls'
        // );
        
        const formattedFiles = files.map(file => ({
          id: file.file_id,
          name: file.filename
        }));
        
        setAvailableFiles(formattedFiles);
        console.log('[GENERATE_REPORT]', 'Available files set:', formattedFiles);
        
      } else {
        console.error('[GENERATE_REPORT]', 'Failed to load files:', result.message);
      }
    } catch (error) {
      console.error('[GENERATE_REPORT]', 'Load files error:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.value;
    setSelectedFile(file);
    console.log('[GENERATE_REPORT]', 'File selected:', file);
  };

  const handleBaseYearChange = (event) => {
    const year = event.target.value;
    setBaseYear(year);
    // 연도가 변경되면 과년도 파일 선택 초기화
    setYearlyFiles({});
    console.log('[GENERATE_REPORT]', 'Base year changed:', year);
  };

  const handleYearlyFileSelect = (year, event) => {
    const fileId = event.target.value;
    setYearlyFiles(prev => ({
      ...prev,
      [year]: fileId || undefined // 빈 값이면 undefined로 설정하여 제거
    }));
    console.log('[GENERATE_REPORT]', `Yearly file selected for ${year}:`, fileId);
  };

  // 병원 목록 데이터 선택
  const handleHosMemberFileSelect = (event) => {
    const file = event.target.value;
    setSelectedMembersFile(file);
    console.log('[GENERATE_REPORT]', 'Hos Member File selected:', file);
  };

  const handleTableToggle = (tableId) => {
    setSelectedTables(prev => {
      const isSelected = prev.some(table => table.id === tableId);
      if (isSelected) {
        return prev.filter(table => table.id !== tableId);
      } else {
        const tableToAdd = availableTables.find(table => table.id === tableId);
        return [...prev, tableToAdd];
      }
    });
    console.log('[GENERATE_REPORT]', 'Table toggled:', tableId);
  };

  const handleSelectAllTablesToggle = () => {
    const newSelectAll = !selectAllTables;
    setSelectAllTables(newSelectAll);
    
    if (newSelectAll) {
      setSelectedTables([...availableTables]);
    } else {
      setSelectedTables([]);
    }
    console.log('[GENERATE_REPORT]', 'Select all tables toggled:', newSelectAll);
  };

  const handleReportFileNameChange = (event) => {
    setReportFileName(event.target.value);
  };

  // 🆕 보고서 제목 변경 핸들러 추가
  const handleReportTitleChange = (event) => {
    setReportTitle(event.target.value);
    setReportFileName(event.target.value);
  };

  const handleGenerate = async () => {
    if (!selectedFile) {
      alert('Raw 데이터 파일을 선택해주세요.');
      return;
    }
    
    if (selectedTables.length === 0) {
      alert('생성할 테이블을 선택해주세요.');
      return;
    }

    if (!reportFileName.trim()) {
      alert('보고서 파일명을 입력해주세요.');
      return;
    }

    if (!detailedInputData) {
      alert('보고서 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    if (!baseYear) {
      alert('기준 연도를 입력해주세요.');
      return;
    }

    if (!selectedMembersFile) {
      alert('병원 목록 파일을 선택해주세요.');
      return;
    }

    // 🆕 과년도 데이터 포함 + 보고서 제목 포함 요청 데이터 구성
    const requestData = {
      raw_data_file_id: selectedFile,
      input_data_id: detailedInputData.input_data_id,
      selected_table_ids: selectedTables.map(table => table.id),
      output_filename: reportFileName,
      header_rows: 6,
      // 과년도 데이터 정보
      base_year: parseInt(baseYear),
      yearly_files: yearlyFiles,
      // 병원 목록 파일
      hos_member_file_id: selectedMembersFile || null,
      // 🆕 보고서 제목 추가
      report_title: reportTitle.trim() || '자동 생성 보고서'
    };
    
    console.log('[GENERATE_REPORT_DEBUG] Request data being sent:', requestData);

    setIsGenerating(true);
    setGenerationMessage('보고서 생성 작업을 시작하고 있습니다.');
    setGenerationProgress({
      phase: 'starting',
      label: '작업 시작 중',
      current: 0,
      total: selectedTables.length,
      percent: 0
    });

    try {
      console.log('[GENERATE_REPORT]', 'Starting report generation job:', requestData);

      const startJobResult = await reportsAPI.createGenerateJob(requestData);
      if (!startJobResult.success) {
        throw new Error(startJobResult.message);
      }

      const jobId = startJobResult.data.job_id;
      if (!jobId) {
        throw new Error('보고서 생성 작업 ID를 받지 못했습니다.');
      }

      setGenerationMessage('보고서 생성 작업이 시작되었습니다.');
      setGenerationProgress({
        phase: 'queued',
        label: '대기 중',
        current: 0,
        total: selectedTables.length,
        percent: 0
      });

      let completedJob = null;
      const maxPollCount = 240; // 5초 × 240 = 최대 20분
      const pollIntervalMs = 5000;

      for (let attempt = 0; attempt < maxPollCount; attempt += 1) {
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
        }

        const jobResult = await reportsAPI.getGenerateJob(jobId);
        if (!jobResult.success) {
          throw new Error(jobResult.message);
        }

        const job = jobResult.data.job;
        if (!job) {
          throw new Error('보고서 생성 작업 상태를 확인할 수 없습니다.');
        }

        setGenerationMessage(job.message || '보고서를 생성하고 있습니다.');
        setGenerationProgress(job.progress || null);

        if (job.status === 'completed') {
          completedJob = job;
          break;
        }

        if (job.status === 'failed') {
          throw new Error(job.message || '보고서 생성 작업이 실패했습니다.');
        }
      }

      if (!completedJob) {
        throw new Error('보고서 생성 작업 확인 시간이 초과되었습니다.');
      }

      const reportData = completedJob.result;
      if (!reportData || !reportData.output_filename) {
        throw new Error('생성된 보고서 파일 정보를 받지 못했습니다.');
      }

      console.log('[GENERATE_REPORT_DEBUG] Report generation job result:', reportData);

      alert(`보고서 생성이 완료되었습니다!\n` +
            `보고서: ${detailedInputData.name}\n` +
            `보고서 제목: ${requestData.report_title}\n` +
            `기준 연도: ${baseYear}\n` +
            `선택된 테이블: ${selectedTables.length}개\n` +
            `출력 파일: ${reportData.output_filename}`);

      // 보고서 다운로드
      try {
        console.log('[GENERATE_REPORT_DEBUG] Starting download with filename:', reportData.output_filename);
        const downloadResult = await reportsAPI.downloadReport(reportData.output_filename);

        if (downloadResult.success) {
          console.log('[GENERATE_REPORT_DEBUG] Download successful');
        } else {
          console.error('[GENERATE_REPORT_DEBUG] Download failed:', downloadResult.message);
          alert(`보고서는 생성되었지만 다운로드 중 오류가 발생했습니다: ${downloadResult.message}`);
        }
      } catch (downloadError) {
        console.error('[GENERATE_REPORT]', 'Download error:', downloadError);
        alert('보고서는 생성되었지만 다운로드 중 오류가 발생했습니다.');
      }

      // 생성 완료 후 Generate 페이지로 돌아가기
      navigate('/generate');

    } catch (error) {
      console.error('[GENERATE_REPORT]', 'Generation error:', error);

      if (error.response) {
        console.error('[GENERATE_REPORT_DEBUG] Error response:', error.response.data);
        console.error('[GENERATE_REPORT_DEBUG] Error status:', error.response.status);
      }

      alert(`보고서 생성 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsGenerating(false);
      setGenerationMessage('');
      setGenerationProgress(null);
    }
  };

  const handleBackToList = () => {
    navigate('/generate');
  };

  // selectAllTables 상태 동기화
  useEffect(() => {
    if (selectedTables.length === availableTables.length && availableTables.length > 0) {
      setSelectAllTables(true);
    } else {
      setSelectAllTables(false);
    }
  }, [selectedTables, availableTables]);

  // 로딩 중 또는 데이터 없을 때 처리
  if (!inputDataInfo) {
    return null; // 리다이렉트 중
  }

  if (isLoading) {
    return (
      <MainLayout>
        <S.ContentSection>
          <S.GeneratingOverlay>
            <S.GeneratingContent>
              <S.GeneratingSpinner />
              <S.GeneratingText>보고서 정보를 불러오는 중...</S.GeneratingText>
            </S.GeneratingContent>
          </S.GeneratingOverlay>
        </S.ContentSection>
      </MainLayout>
    );
  }

  if (!detailedInputData) {
    return (
      <MainLayout>
        <S.ContentSection>
          <S.Header>
            <S.BackButton onClick={handleBackToList}>
              ← 보고서 목록으로 돌아가기
            </S.BackButton>
            <S.ReportTitle>데이터를 불러올 수 없습니다</S.ReportTitle>
            <S.ReportDescription>
              보고서 정보를 불러오는 중 오류가 발생했습니다.
            </S.ReportDescription>
          </S.Header>
        </S.ContentSection>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <S.ContentSection>
        <S.Header>
          <S.BackButton onClick={handleBackToList}>
            ← 보고서 목록으로 돌아가기
          </S.BackButton>
          <S.ReportTitle>{detailedInputData.name}</S.ReportTitle>
          <S.ReportDescription>
            {detailedInputData.description || `${detailedInputData.tables_count}개의 테이블이 포함된 보고서입니다.`}
          </S.ReportDescription>

          {/* 🆕 보고서 제목 입력 섹션 추가 */}
          <S.ReportTitleSection>
            <S.ReportTitleLabel>보고서 제목</S.ReportTitleLabel>
            <S.ReportTitleInput
              type="text"
              value={reportTitle}
              onChange={handleReportTitleChange}
              placeholder="보고서 첫 페이지에 표시될 제목을 입력하세요.(미입력시 ‘자동생성 보고서’로 표기)"
              disabled={isGenerating}
            />
          </S.ReportTitleSection>
        </S.Header>

        {isGenerating && (
          <S.GeneratingOverlay>
            <S.GeneratingContent>
              <S.GeneratingSpinner />
              <S.GeneratingText>
                {generationProgress?.label || '보고서를 생성하고 있습니다.'}
              </S.GeneratingText>
              <S.GeneratingSubtext>
                {generationMessage || '작업 상태를 확인하고 있습니다.'}
              </S.GeneratingSubtext>
              {generationProgress && (
                <S.GeneratingSubtext>
                  진행률 {generationProgress.percent ?? 0}% · {generationProgress.current ?? 0}/{generationProgress.total ?? selectedTables.length}개
                </S.GeneratingSubtext>
              )}
              <S.GeneratingSubtext>
                화면을 닫지 않으면 완료 후 자동으로 다운로드됩니다.
              </S.GeneratingSubtext>
            </S.GeneratingContent>
          </S.GeneratingOverlay>
        )}

        <GenerateModal
          selectedFile={selectedFile}
          selectedReports={selectedTables}
          selectAllReports={selectAllTables}
          reportFileName={reportFileName}
          availableFiles={availableFiles}
          availableReports={availableTables}
          // 과년도 데이터 관련 props 전달
          baseYear={baseYear}
          yearlyFiles={yearlyFiles}
          // 병원 목록 데이터 관련 props 전달
          selectedMembersFile={selectedMembersFile}
          // 핸들러 전달
          onFileSelect={handleFileSelect}
          onReportToggle={handleTableToggle}
          onSelectAllReportsToggle={handleSelectAllTablesToggle}
          onReportFileNameChange={handleReportFileNameChange}
          // 과년도 데이터 관련 핸들러 전달
          onBaseYearChange={handleBaseYearChange}
          onYearlyFileSelect={handleYearlyFileSelect}
          onGenerate={handleGenerate}
          disabled={isGenerating}
          // 병원 목록 데이터 관련 핸들러 전달
          onHosMemberFileSelect={handleHosMemberFileSelect}
        />
      </S.ContentSection>
    </MainLayout>
  );
};

export default GenerateReport;